/**
 * Etiket yazıcısı seçimi.
 *
 * Üç kanal da aynı listede toplanır:
 *   USB doğrudan — usbprint.sys üzerinden, üretici driver'ı gerekmez
 *   Kurulu yazıcı — Windows spooler'a RAW datatype ile gönderilir
 *   Ağ yazıcısı — tcp://host:port, spooler devrede değil
 *
 * Driver kurulu bir yazıcı iki kez görünebilir (hem USB hem spooler); ikisi de
 * çalışır, USB doğrudan yolu bir katman az olduğu için önce listelenir.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UsbIcon from '@mui/icons-material/Usb';
import PrintIcon from '@mui/icons-material/Print';
import LanIcon from '@mui/icons-material/Lan';
import {
  LANGUAGE_LABELS,
  LabelPrinterSelection,
  RAW_SUPPORTED_LANGUAGES,
  SystemPrinterInfo,
  UsbPrinterInfo,
  listSystemPrinters,
  listUsbPrinters,
  loadSelectedPrinter,
  saveSelectedPrinter,
} from '../../../services/labelPrintService';
import { PrinterLanguage } from '../../../types/labelTemplate.types';

export interface LabelPrinterSelectProps {
  value: LabelPrinterSelection | null;
  onChange: (selection: LabelPrinterSelection | null) => void;
}

const MANUAL_VALUE = '__manual__';

/**
 * Yazıcının hangi dili konuştuğu kullanıcıya sorulur: ne USB aygıt listesi ne
 * spooler bu bilgiyi veriyor, üstelik aynı model farklı firmware'lerle
 * satılıyor (Argox'ta PPLA/PPLB/PPLZ). Yanlış dil seçilirse yazıcı komutları
 * yok sayar ve boş etiket çıkar; teşhis bilgisi olarak yapılandırma etiketi
 * öneriliyor.
 */
const DEFAULT_LANGUAGE: PrinterLanguage = 'PPLA';

const LabelPrinterSelect: React.FC<LabelPrinterSelectProps> = ({ value, onChange }) => {
  const [printers, setPrinters] = useState<UsbPrinterInfo[]>([]);
  const [systemPrinters, setSystemPrinters] = useState<SystemPrinterInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [manualTarget, setManualTarget] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // İki liste bağımsız; biri boş dönse diğeri kullanılabilir kalmalı
      const [usb, system] = await Promise.all([listUsbPrinters(), listSystemPrinters()]);
      setPrinters(usb);
      setSystemPrinters(system);
      if (usb.length === 0 && system.length === 0) {
        setError('Yazıcı bulunamadı. Yazıcının açık ve bağlı olduğundan emin olun.');
      }
    } catch (err) {
      setPrinters([]);
      setError(err instanceof Error ? err.message : 'Yazıcı listesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  // Açılışta kayıtlı seçimi geri yükle ve listeyi tazele
  useEffect(() => {
    if (!value) {
      const saved = loadSelectedPrinter();
      if (saved) onChange(saved);
    }
    refresh();
    // Yalnızca ilk açılışta çalışmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (selected: string) => {
    if (selected === MANUAL_VALUE) {
      setManual(true);
      return;
    }
    setManual(false);

    const usb = printers.find((p) => p.devicePath === selected);
    const base: LabelPrinterSelection = usb
      ? { devicePath: usb.devicePath, label: usb.description }
      : { printerName: selected, label: selected };

    // Dil seçimi yazıcıya bağlı; hedef değişse de kullanıcının seçimi korunur
    const selection: LabelPrinterSelection = {
      ...base,
      language: value?.language ?? DEFAULT_LANGUAGE,
    };

    saveSelectedPrinter(selection);
    onChange(selection);
  };

  const handleLanguage = (language: PrinterLanguage) => {
    if (!value) return;
    const selection: LabelPrinterSelection = { ...value, language };
    saveSelectedPrinter(selection);
    onChange(selection);
  };

  /**
   * Select'in göstereceği değer. Kayıtlı seçim listede yoksa (yazıcı çıkarılmış
   * ya da hedef elle girilmiş) "elle hedef" satırına düşülür; aksi halde MUI
   * aralık dışı değer uyarısı veriyor.
   */
  const selectValue = value?.devicePath
    ? printers.some((p) => p.devicePath === value.devicePath)
      ? value.devicePath
      : ''
    : value?.printerName
      ? systemPrinters.some((p) => p.name === value.printerName)
        ? value.printerName
        : MANUAL_VALUE
      : '';


  const handleManualApply = () => {
    const target = manualTarget.trim();
    if (!target) return;
    const selection: LabelPrinterSelection = { printerName: target, label: target };
    saveSelectedPrinter(selection);
    onChange(selection);
    setManual(false);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          size="small"
          label="Etiket Yazıcısı"
          fullWidth
          value={selectValue}
          onChange={(e) => handleSelect(e.target.value)}
        >
          {/* Koşulsuz: seçim listede yoksa Select'in tutunacağı bir değer kalmalı */}
          <MenuItem value="" disabled>
            {loading
              ? 'Aranıyor…'
              : printers.length === 0 && systemPrinters.length === 0
                ? 'Yazıcı bulunamadı'
                : 'Seçiniz'}
          </MenuItem>

          {printers.length > 0 && <ListSubheader>USB (driver gerekmez)</ListSubheader>}
          {printers.map((p) => (
            <MenuItem key={p.devicePath} value={p.devicePath}>
              <Stack direction="row" spacing={1} alignItems="center">
                <UsbIcon fontSize="small" color="action" />
                <span>
                  {p.description}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {p.vendorId}:{p.productId}
                  </Typography>
                </span>
              </Stack>
            </MenuItem>
          ))}

          {systemPrinters.length > 0 && <ListSubheader>Kurulu ve ağ yazıcıları</ListSubheader>}
          {systemPrinters.map((p) => (
            <MenuItem key={p.name} value={p.name}>
              <Stack direction="row" spacing={1} alignItems="center">
                {p.isNetwork ? (
                  <LanIcon fontSize="small" color="action" />
                ) : (
                  <PrintIcon fontSize="small" color="action" />
                )}
                <span>{p.name}</span>
              </Stack>
            </MenuItem>
          ))}

          <MenuItem value={MANUAL_VALUE}>Elle hedef gir…</MenuItem>
        </TextField>

        <Button
          size="small"
          onClick={refresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          sx={{ mt: 0.25, flexShrink: 0 }}
        >
          Yenile
        </Button>
      </Stack>

      {manual && (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Hedef"
            placeholder="tcp://192.168.1.50 veya Windows yazıcı adı"
            value={manualTarget}
            onChange={(e) => setManualTarget(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualApply()}
          />
          <Button size="small" onClick={handleManualApply} disabled={!manualTarget.trim()}>
            Kullan
          </Button>
        </Stack>
      )}

      {value && (
        <TextField
          select
          size="small"
          label="Yazıcı Dili"
          fullWidth
          value={value.language ?? DEFAULT_LANGUAGE}
          onChange={(e) => handleLanguage(e.target.value as PrinterLanguage)}
          helperText='Yazıcının dilini yapılandırma etiketinden görebilirsiniz: yazıcıyı FEED tuşu basılı tutarak açın. Boş etiket çıkıyorsa dil yanlış seçilmiş demektir.'
        >
          {RAW_SUPPORTED_LANGUAGES.map((lang) => (
            <MenuItem key={lang} value={lang}>
              {LANGUAGE_LABELS[lang]}
            </MenuItem>
          ))}
        </TextField>
      )}

      {/* Elle girilen hedef listede görünmediği için ayrıca yazılır */}
      {value?.printerName && selectValue === MANUAL_VALUE && !manual && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Hedef: {value.printerName}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="info" sx={{ py: 0 }}>
          {error}
        </Alert>
      )}
    </Stack>
  );
};

export default LabelPrinterSelect;
